'use strict'
import React, { } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import {
    getComponentWithId,
    getLabel,
} from '../../utils/StoreUtils'
import ExportAction from '../../export/actions/ExportAction'
import ToastrAction from 'toastr/actions/ToastrAction'
import { getSiteMarker, getSiteUrl } from '../../utils/mapUtils/SiteTypes'
import { STATION_NAME_ASSOCIATION, STATION_TYPE_NAME } from '../../station/constants/StationConstants'
import { path } from '../../conf/basepath'
import { getDate } from '../../utils/DateUtil'
import {
    filterStationCoordinates,
    filterStationsCoordinates,
    getProjectionsList,
    getWGS84Coordinate,
} from '../../utils/mapUtils/CoordinateUtils'
import DtoAnalysisTableItem from '../../quality/dto/DtoAnalysisTableItem'
import { groupBy, orderBy, toUpper } from 'lodash'
import CityDto from '../../referencial/components/city/dto/CityDto'
import PropTypes from 'prop-types'
import Map from 'ol/Map'
import { geti18n } from '../../utils/StringUtil'
import { analysisHasLocalisation, execByType, getStationType } from '../../utils/StationUtils'
import { Icon, List, ListItem, ListItemIcon, ListItemText } from '@mui/material'
import { INSTALLATION_TYPES } from '../../installation/constants/InstallationConstants'
import DtoQualitometer from 'quality/dto/DtoQualitometer'
import { getColor, hexToRGBColor } from 'utils/ColorUtil'


const CartographyExportPanel = ({
    layers,
    station,
    analysis,
    stationsPoints = [],
    onClose,
    visible,
}) => {
    const {
        externalSites,
        sitesTypes,
        citiesIndex,
        cities,
        qualitometersLight,
    } = useSelector(store => ({
        externalSites: store.StationReducer.externalSites,
        sitesTypes: store.StationReducer.sitesTypes,
        citiesIndex: store.CityReducer.citiesIndex,
        cities: store.CityReducer.cities,
        qualitometersLight: store.QualityReducer.qualitometersLight,
    }))

    const dispatch = useDispatch()

    const getBaseUrl = () => {
        return path.replace('api/', '')
    }

    const formatData = (body) => {
        const header = {}
        body[0].headers.map(e => {
            header[e] = { value: (() => {
                try {
                    return i18n[e]
                } catch (err) {
                    return e
                }
            })(),
            }
        })

        return [header].concat(body).map(line => {
            return body[0].headers.map(key => {
                if (line[key] || line[key] == 0) {
                    if (line[key].value) {
                        return { ...line[key], value: line[key].value.toString() }
                    }
                    return { value: line[key].toString() }
                }
                return { value: '' }
            })
        })
    }

    const getStationsPoints = () => {
        const points = filterStationsCoordinates(stationsPoints, citiesIndex)
            .map(point => {
                const loc = getWGS84Coordinate(point)
                return Object.assign({}, point, { localisation: { x: loc[0], y: loc[1], projection: 16 } })
            })
        return points.filter(p => execByType(p.typeName, {
            quality: () => visible.QUALITOMETER || visible[`quality${p.stationType}`],
            installation: () => visible.INSTALLATION || visible[`installationType${p.installationType}`],
            piezometry: () => visible.PIEZOMETER,
            pluviometry: () => visible.PLUVIOMETER,
            hydrometry: () => visible.HYDROMETRIC_STATION,
            default: () => false,
        })).map(point => {
            const stationType = execByType(point.typeName, {
                quality: () => point.stationType,
                installation: () => {
                    const installationType= INSTALLATION_TYPES.find(type => type.code === point.installationType)
                    if (!installationType) {
                        return ''
                    }
                    return i18n[installationType.label]
                },
                default: () =>'',
            })
            return {
                code: { value: point.typeName === STATION_TYPE_NAME.installation ? point.id.toString() : point.code || '', cellType: 'string' },
                name: point.name || '',
                cityCode: point.townCode || '',
                city: point.townCode ? getLabel(cities, point.townCode) : '',
                location: point.location,
                type: i18n[point.typeName],
                x: point.localisation.x,
                y: point.localisation.y,
                projection: point.localisation.projection,
                stationType,
                startDate: point.creationDate ? getDate(point.creationDate) : '',
                endDate: point.close ? getDate(point.close) : '',
                marker: point.markerIcon || (getBaseUrl() + getSiteUrl(STATION_NAME_ASSOCIATION[point.typeName])),
            }
        })
    }

    const getExternalSites = () => {
        if (layers && (layers.includes('POLLUTED_SOIL') || layers.includes('INDUSTRIAL_SITE'))) {
            return externalSites.map(site => ({
                code: { value: site.code, cellType: 'string' },
                name: site.name || '',
                location: '',
                city: site.townCode ? getLabel(cities, site.townCode, 'labelWithCode') : '',
                type: geti18n(site.typeName),
                x: site.xCoordinate,
                y: site.yCoordinate,
                projection: site.projection,
                stationType: getLabel(sitesTypes, site.type_id, 'definition', 'id'),
                startDate: '',
                endDate: '',
                marker: site.markerIcon || (getBaseUrl() + getSiteUrl(getLabel(sitesTypes, site.type_id, 'parameter', 'id'))),
            }))
        }
        return []
    }

    const getStation = () => {
        const filteredStation = filterStationCoordinates(station, citiesIndex)
        if (filteredStation && filteredStation.code) {
            const loc = getWGS84Coordinate(station)
            return [{
                code: { value: filteredStation.typeName === STATION_TYPE_NAME.installation ? filteredStation.id.toString() : filteredStation.code || '', cellType: 'string' },
                name: filteredStation.name || '',
                cityCode: { value: filteredStation.townCode, cellType: 'right' },
                city: filteredStation.townCode ? getLabel(cities, filteredStation.townCode) : '',
                location: filteredStation.location,
                type: geti18n(filteredStation.typeName),
                x: { value: loc[0], format: '0.0000000000', cellType: 'number' },
                y: { value: loc[1], format: '0.0000000000', cellType: 'number' },
                projection: { value: 16, format: '0', cellType: 'number' },
                stationType: getStationType(parseInt((filteredStation.stationType))).libelle,
                startDate: filteredStation.creationDate ? getDate(filteredStation.creationDate) : '',
                endDate: filteredStation.close ? getDate(filteredStation.close) : '',
                marker: filteredStation.markerIcon || (getBaseUrl() + getSiteUrl(STATION_NAME_ASSOCIATION[filteredStation.typeName])),
            }]
        }
        return []
    }

    const getAnalysis = () => {
        if (analysis && analysis.length) {
            const groupedAnalysis = groupBy(analysis, 'qualitometer')
            return Object.keys(groupedAnalysis).map(code => {
                const analyses = orderBy(groupedAnalysis[code], ['date'], ['desc'])
                const found = qualitometersLight.find(q => q.id === analyses[0].qualitometer)
                const color = getColor(hexToRGBColor(analyses[0].color))
                return {
                    code: { value: found?.code, cellType: 'string' },
                    name: { value: found?.name, cellType: 'string' },
                    cityCode: { value: found?.townCode, cellType: 'right' },
                    city: found ? getLabel(cities, found?.townCode) : '',
                    location: found?.location,
                    type: geti18n(analyses[0].typeName),
                    x: { value: found?.x, format: '0.0000000000', cellType: 'number' },
                    y: { value: found?.y, format: '0.0000000000', cellType: 'number' },
                    projection: { value: found?.projection ? getProjectionsList().find(p => p.sandreCode === found?.projection)?.name : '', cellType: 'string' },
                    stationType: found?.stationType,
                    startDate: getDate(found?.creationDate),
                    endDate: getDate(found?.close),
                    marker: analyses[0].markerIcon || (getBaseUrl() + getSiteUrl(`QUALITOMETER_${toUpper(color)}`)),
                }
            })
        }
        return []
    }

    const onExportMarkersKML = () => {
        // const data = [].concat(getStation(), getStationsPoints(), getExternalSites(), getAnalysis())
        const component = getComponentWithId('#sieau-map')
        if (component) {
            const data = component.getLayers().filter(l => l.objPoints && l.objPoints.site && l.layer?.values_?.visible).flatMap(l => l.objPoints.site).map(obj => ({
                code: obj.code || '',
                city: `${obj.city?.value}` || `${obj.city}` || `${obj.cityLabel?.value}` || `${obj.cityLabel}` || `${obj.townCode}` || '',
                name: obj.name || '',
                location: obj.location || '',
                stationType: obj.stationType?.toString() || '',
                x: obj.x,
                y: obj.y,
                projection: obj.projection,
                startDate: obj.startDate || '',
                endDate: obj.endDate || '',
                marker: obj.markerIcon || (getBaseUrl() + (obj.markerIcon || getSiteMarker(obj))),
            }))
            const usedData = data.filter(d => analysisHasLocalisation(d) && d.marker)
            if (usedData.length) {
                dispatch(ExportAction.kmlExport(usedData))
            } else {
                dispatch(ToastrAction.error(i18n.noDataToExport))
            }
            onClose()
        }
    }

    const onExportMarkersXLS = () => {
        const data = [...getStation(), ...getStationsPoints(), ...getExternalSites(), ...getAnalysis()]
        if (data.length) {
            const exportData = {
                data: data.map(d => ({ ...d, headers: ['code', 'name', 'cityCode', 'city', 'type', 'stationType', 'x', 'y', 'projection', 'marker'] })),
                exportType: 'xlsx',
                titleFile: i18n.mapMarkers,
            }
            dispatch(ExportAction.export(formatData(exportData.data), exportData.exportType, exportData.titleFile))
        } else {
            dispatch(ToastrAction.error(i18n.noDataToExport))
        }
        onClose()
    }

    const onExportMarkersCSV = () => {
        const data = [...getStation(), ...getStationsPoints(), ...getExternalSites(), ...getAnalysis()]

        if (data.length) {
            const exportData = {
                data: data.map(d => ({ ...d, headers: ['code', 'name', 'cityCode', 'city', 'type', 'stationType', 'x', 'y', 'projection', 'marker'] })),
                exportType: 'csv',
                titleFile: i18n.mapMarkers,
            }
            dispatch(ExportAction.export(formatData(exportData.data), exportData.exportType, exportData.titleFile))
        } else {
            dispatch(ToastrAction.error(i18n.noDataToExport))
        }
        onClose()
    }

    // const onExportPNG = () => {
    //     map.once('postcompose', event => {
    //         const canvas = event.context.canvas
    //         canvas.toBlob(blob => {
    //             download(blob, 'map.png', blob.type)
    //         })
    //     })
    //     map.renderSync()
    // }

    return (
        <List className='clickable'>
            <ListItem button onClick={onExportMarkersXLS}>
                <ListItemIcon>
                    <Icon fontSize='large'>list</Icon>
                </ListItemIcon>
                <ListItemText primary={ i18n.xlsExport } />
            </ListItem>
            <ListItem button onClick={onExportMarkersCSV}>
                <ListItemIcon>
                    <Icon fontSize='large'>description</Icon>
                </ListItemIcon>
                <ListItemText primary={i18n.csvExport} />
            </ListItem>
            <ListItem button onClick={onExportMarkersKML}>
                <ListItemIcon>
                    <Icon fontSize='large'>map</Icon>
                </ListItemIcon>
                <ListItemText primary={i18n.kmlExport} />
            </ListItem>
        </List>
    )
}

CartographyExportPanel.propTypes = ({
    analysis: PropTypes.arrayOf(PropTypes.instanceOf(DtoAnalysisTableItem)),
    stationsPoints: PropTypes.arrayOf(PropTypes.instanceOf(DtoQualitometer)),
    station: PropTypes.shape({}),
    layers: PropTypes.arrayOf(PropTypes.string),
    externalSites: PropTypes.arrayOf(PropTypes.shape({})),
    sitesTypes: PropTypes.arrayOf(PropTypes.shape({})),
    citiesIndex: PropTypes.instanceOf(CityDto),
    map: PropTypes.instanceOf(Map),
    onClose: PropTypes.func,
    visible: PropTypes.shape({
        DISTRIBUTION_UNIT: PropTypes.bool,
        HYDROMETRIC_STATION: PropTypes.bool,
        INSTALLATION: PropTypes.bool,
        PIEZOMETER: PropTypes.bool,
        PLUVIOMETER: PropTypes.bool,
        PRODUCTION_UNIT: PropTypes.bool,
        QUALITOMETER: PropTypes.bool,
        installationType0: PropTypes.bool,
        installationType1: PropTypes.bool,
        installationType2: PropTypes.bool,
        installationType3: PropTypes.bool,
        installationType4: PropTypes.bool,
        installationType5: PropTypes.bool,
        installationType6: PropTypes.bool,
        installationType7: PropTypes.bool,
        installationType8: PropTypes.bool,
        installationType9: PropTypes.bool,
        installationType10: PropTypes.bool,
        installationType11: PropTypes.bool,
        installationType12: PropTypes.bool,
        installationType13: PropTypes.bool,
        installationType14: PropTypes.bool,
        installationType16: PropTypes.bool,
        installationType17: PropTypes.bool,
        installationType18: PropTypes.bool,
        installationType20: PropTypes.bool,
        'installationType-1': PropTypes.bool,
        qualitometerType0: PropTypes.bool,
        qualitometerType1: PropTypes.bool,
        qualitometerType2: PropTypes.bool,
        qualitometerType3: PropTypes.bool,
        qualitometerType4: PropTypes.bool,
        qualitometerType5: PropTypes.bool,
        qualitometerType6: PropTypes.bool,
        qualitometerType7: PropTypes.bool,
        qualitometerType8: PropTypes.bool,
    }),
})

export default CartographyExportPanel
